import { batch } from "solid-js";
import { reconcile } from "solid-js/store";
import { useSearchParams } from "@solidjs/router";
import FeatureStore from "@rock-solid/feature-store";

import Admin, { AdminUsa } from "@repo/kikoff-models/Admin";
import CommunityPost from "@repo/kikoff-models/community/CommunityPost";
import CommunityProfile, {
  CommunityProfileUsaAdmin,
} from "@repo/kikoff-models/community/CommunityProfile";
import User from "@repo/kikoff-models/User";
import adminApi from "@repo/rest/kikoffAdminApi";

import { usePosts } from "./posts";
import { useProfiles } from "./profiles";

const initialState = {
  admin: null as Admin | null,
  profiles: null as CommunityProfile.Id[] | null,
  currentProfile: null as CommunityProfile.Id | null,
  userByPostToken: {} as Record<CommunityPost.Id, User.Id>,
};

export const { useAdmin } = FeatureStore.init("Admin", initialState).setup(
  ([store, setStore], { attachAction, wrap }) => {
    const [profiles, setProfiles] = useProfiles();
    const [posts, setPosts] = usePosts();

    const [searchParams] = useSearchParams();
    const invalidateHome = () =>
      setPosts.fetchPage(
        1,
        searchParams.sort as CommunityPost.SortMethod,
        searchParams.q as string
      );

    const accessors = wrap
      .accessors(
        Object.assign(() => store.admin, {
          profiles: Object.assign(
            attachAction(
              () => store.profiles?.map((id) => profiles.byId(id)!),
              () => actions.profiles.fetch()
            ),
            {
              current: () => profiles.byId(store.currentProfile!),
            }
          ),
          userFromPost: attachAction(
            (id: CommunityPost.Id) => store.userByPostToken[id],
            (id) => actions.post.getUser(id)
          ),
        })
      )
      .finalize();

    const mutations = wrap
      .mutations({
        admin(admin: Admin) {
          setStore("admin", reconcile(admin));
        },
      })
      .finalize();

    const actions = wrap
      .actions({
        fetch() {
          return adminApi["admin-info"].GET({}).then((res) => {
            mutations.admin(AdminUsa.normalize(res));
          });
        },
        profiles: {
          current(id: CommunityProfile.Id | null) {
            setStore("currentProfile", id);
          },
          fetch() {
            return adminApi.community.profiles.GET({}).then((res) => {
              const normalized = res.profiles.map((p) =>
                CommunityProfileUsaAdmin.normalize(p)
              );

              batch(() => {
                setProfiles.many(normalized);
                setProfiles.manySummaries(normalized);
                setStore(
                  "profiles",
                  normalized.map(({ id }) => id)
                );
              });
            });
          },
          create(params: {
            firstName: string;
            lastName: string;
            state: string;
          }) {
            return adminApi.community.profile
              .POST(params)
              .then(({ profile }) => {
                batch(() => {
                  const normalized =
                    CommunityProfileUsaAdmin.normalize(profile);
                  setProfiles.one(normalized);
                  setProfiles.oneSummary(normalized);
                  setStore(
                    "profiles",
                    (profiles) => profiles && [...profiles, normalized.id]
                  );
                  setStore("currentProfile", normalized.id);
                });
              });
          },
          ban(id: CommunityProfile.Id) {
            return adminApi.community.profile[id]!["ban-status"].POST({
              banned: true,
            });
          },
        },
        post: {
          create({
            parentId,
            title,
            bodyMd,
            anonymous,
            showScore,
          }: CommunityPost.Params) {
            if (!store.currentProfile)
              throw new Error(
                "Attempted to call setAdmin.post.create without admin created profile selected."
              );
            if (anonymous)
              throw new Error("Admin profile cannot post anonymously.");
            if (showScore)
              throw new Error("Admin profile cannot post with score.");

            return adminApi.community.post
              .POST({
                profileToken: store.currentProfile,
                title,
                bodyMd,
                parentPostToken: parentId,
              })
              .then(async ({ postToken }) => {
                await setPosts.fetch(
                  parentId
                    ? posts.rootIdFor(parentId)
                    : (postToken as CommunityPost.Id)
                );

                return posts.byId(postToken as CommunityPost.Id)!;
              });
          },

          hide(id: CommunityPost.Id) {
            return adminApi.community.post[id]!.status.POST({
              status: "hidden",
            }).then(() => {
              const rootId = posts.rootIdFor(id);
              if (rootId !== id) setPosts.fetch(rootId);
              else invalidateHome();
            });
          },

          pin(id: CommunityPost.Id) {
            return adminApi.community.post[id]!.pin.POST({}).then(() => {
              return invalidateHome();
            });
          },

          unpin(id: CommunityPost.Id) {
            return adminApi.community.post[id]!.unpin.POST({}).then(() => {
              return invalidateHome();
            });
          },

          updateVotes(id: CommunityPost.Id, addedVotes: number) {
            return adminApi.community.post[id]!.update_votes.POST({
              votes: addedVotes,
            }).then(() => {
              const post = posts.byId(id);
              if (post) {
                setPosts.updateVotes(id, addedVotes);
              }
            });
          },

          getUser(id: CommunityPost.Id) {
            return adminApi.community.post[id]!.get_user.GET({}).then((res) => {
              setStore("userByPostToken", id, res.userToken);
            });
          },
        },
      })
      .finalize();

    return { accessors, actions };
  }
);
